'use client';

import React from 'react';
import { useIntl } from 'react-intl';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { text5 } from '@osmosis/tailwind-ui/styles/text';
import { BreadcrumbV2Object } from '@osmosis/shared/types/learn';
import { usePagesContext } from '@osmosis/nextjs-components/context';
import { useContentSize } from '@osmosis/nextjs-components/context/ContentSizeContext';

import BreadcrumbsV2 from '../../BreadcrumbsV2/BreadcrumbsV2';
import { getBreadcrumbTrackHandler } from '../../../lib/tracking';

type Props = {
  /** Library breadcrumb paths for this learn node. */
  breadcrumbsv2?: BreadcrumbV2Object[];
  /** Title of the current page, appended (non-link) at the end of the trail. */
  currentTitle: string;
};

/**
 * Orders the breadcrumb paths so the one matching the `from` query param (where
 * the user navigated from) comes first, preserving the order of the rest.
 * Mirrors the v1 `LearnBreadcrumbs` reordering behavior.
 */
function orderBreadcrumbs(
  breadcrumbsv2: BreadcrumbV2Object[],
  from?: string,
): BreadcrumbV2Object[] {
  const fromIndex = breadcrumbsv2.findIndex(({ link }) => link === from);
  if (fromIndex <= 0) return breadcrumbsv2;
  const reordered = [...breadcrumbsv2];
  const [fromMatch] = reordered.splice(fromIndex, 1);
  reordered.unshift(fromMatch);
  return reordered;
}

/**
 * V2 Learn page breadcrumb trail. Renders every library path (separated by a
 * chevron) followed by the current page title in muted text. Wraps to multiple
 * lines when space is tight. Desktop (md+) only — hidden on mobile via CSS, but
 * kept in the DOM for crawlers.
 */
const LearnBreadcrumbs = ({
  breadcrumbsv2 = [],
  currentTitle,
}: Props): JSX.Element | null => {
  const { formatMessage } = useIntl();
  const { query } = usePagesContext();
  const { getClassValue } = useContentSize();

  if (!breadcrumbsv2.length) return null;

  const orderedBreadcrumbs = orderBreadcrumbs(
    breadcrumbsv2,
    query.from?.toString(),
  );

  const visibility = getClassValue({
    defaultClasses: 'hidden md:flex',
    breakpoints: { md: 'flex', base: 'hidden' },
  });

  return (
    <Flex
      data-testid="learn-breadcrumbs"
      aria-label={formatMessage(
        {
          id: 'learn.breadcrumbs.section',
          defaultMessage: 'Breadcrumbs for "{sectionName}"',
        },
        { sectionName: currentTitle },
      )}
      className={cn('flex-wrap items-center gap-y-1 !text-sm', visibility)}
    >
      <Flex className="flex-col">
        {orderedBreadcrumbs.map((item) => (
          <Box
            key={item.link}
            className="mb-2"
            data-testid={'breadcrumbs-v2-dropdown-content'}
          >
            <BreadcrumbsV2
              breadcrumb={item}
              linkClassName={cn('!text-sm hover:text-blue-600', text5)}
              onBreadCrumbItemClick={getBreadcrumbTrackHandler('learn-page')}
            />
          </Box>
        ))}
      </Flex>
    </Flex>
  );
};

export default LearnBreadcrumbs;
